import EmptyState from '@/components/EmptyState';
import OrderActions from '@/features/orders/components/OrderActions';
import AcceptOrderSheet from '@/features/orders/components/AcceptOrderSheet';
import CancelOrderSheet from '@/features/orders/components/CancelOrderSheet';
import OrderCard from '@/features/orders/components/OrderCard';
import { useOrders, type OrderFilters } from '@/features/orders/hooks/useOrders';
import { useCompleteOrder } from '@/features/orders/hooks/useOrderMutations';
// Reuse the reservations day-summary histogram - a generic hour-by-hour bar
// chart (its `guests` field is just "the value plotted"; here it's orders/hour).
import CoversByHour, { type HourBar } from '@/features/reservations/components/CoversByHour';
import { orderWhenIso } from '@/features/orders/utils/time';
import { deliveryDistanceKmPreferRoute, formatDistanceKm } from '@/features/orders/utils/distance';
import { useOrderShifts } from '@/features/order-setup/hooks/useOrderSetup';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { fromApi } from '@/utils/datetime';
import { useT, useI18nStore } from '@/i18n';
import type { OnlineOrder, OrderShift, OrderStatus } from '@restaurant/api-client';
import { BlockTitle, Block, List, Preloader, f7 } from 'framework7-react';
import { DateTime } from 'luxon';
import { Fragment, useRef, useState } from 'react';

/** The always-on BASELINE (default) per-slot order cap for a channel, or null
 *  when uncapped / not configured. Both SIMPLE and ADVANCED rules carry
 *  `maxOrdersPerSlot`, so no mode branch is needed. */
function baselineOrderCap(shifts: OrderShift[] | undefined): number | null {
  const baseline = shifts?.find((s) => s.scope === 'BASELINE');
  return baseline ? baseline.rules.maxOrdersPerSlot : null;
}

// Section by raw order STATUS, in operational order: new (pending) → confirmed
// (in progress) → completed → cancelled. The visible label is resolved from
// `t.orders.status` inside the component (module scope - no hook available here).
const STATUS_ORDER: OrderStatus[] = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'];

/**
 * Order list as a *view* - no navbar, no filter bar, no ptr. Renders inside
 * the Orders workspace shell, which owns filters and selection. Tapping a card
 * asks the shell to open the inspector (docked on wide, pushed on phone).
 * Mirrors the reservations `ListView`.
 */
export default function OrderListView({
  filters,
  selectedId,
  onSelectOrder,
}: {
  filters: OrderFilters;
  selectedId: string | null;
  onSelectOrder: (order: OnlineOrder) => void;
}) {
  const t = useT();
  const locale = useI18nStore((s) => s.locale);
  // Restaurant origin for the crow-flies delivery distance shown on each row.
  const restaurant = useRestaurantStore((s) => s.restaurantConfig);
  const [actionTarget, setActionTarget] = useState<OnlineOrder | null>(null);
  // The order whose accept sheet is open (null = closed). Accept opens the sheet
  // (ETA review + tune) rather than firing instantly; the sheet owns the mutation.
  const [acceptTarget, setAcceptTarget] = useState<OnlineOrder | null>(null);
  // The order whose cancel sheet is open (null = closed). Cancel opens the sheet
  // (refund choice for a refundable payment) rather than a bare confirm; the
  // sheet owns the mutation.
  const [cancelTarget, setCancelTarget] = useState<OnlineOrder | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popoverRef = useRef<any>(null);

  const { data, isLoading, isFetching, error } = useOrders(filters);
  // Default (BASELINE) per-slot caps - configured separately per channel, so the
  // card shows each one rather than a single figure the pooled bars can't match.
  const { data: pickupShifts } = useOrderShifts('PICKUP', true);
  const { data: deliveryShifts } = useOrderShifts('DELIVERY', true);

  const completeOrder = useCompleteOrder();

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const handleBadgeTap = (order: OnlineOrder, targetEl: HTMLElement) => {
    setActionTarget(order);
    requestAnimationFrame(() => {
      if (popoverRef.current?.el) f7.popover.open(popoverRef.current.el, targetEl);
    });
  };

  // Accept opens the ETA sheet (prep + travel review) rather than firing instantly,
  // so the owner sees what they're committing to. See AcceptOrderSheet.
  const handleAccept = (o: OnlineOrder) => {
    f7.popover.close();
    setAcceptTarget(o);
  };

  // Same wording split as useOrderActions: a terminal cash order goes down the
  // correction rail (reverses money, points and the visit count), so it asks a
  // different question than a plain complete / cancel.
  const handleComplete = (o: OnlineOrder) => {
    f7.popover.close();
    if (completeOrder.isPending && completeOrder.variables?.orderId === o.id) return;
    const copy =
      o.status === 'CANCELLED' ? t.orders.action.restoreOrder : t.orders.action.completeOrder;
    f7.dialog.confirm(copy.confirm, copy.title, () => {
      completeOrder.mutate(
        { orderId: o.id, track: { action: 'done', order: o, from: 'list' } },
        { onSuccess: () => toast(copy.success) },
      );
    });
  };

  // Cancel opens the sheet (refund choice for a refundable payment) rather than
  // firing a bare confirm. The sheet owns the mutation, toast and toggle.
  const handleCancel = (o: OnlineOrder) => {
    f7.popover.close();
    setCancelTarget(o);
  };

  // Scope to the restaurant's local today. The server already does this (it
  // receives `day` and bounds by each order's service instant in the restaurant
  // timezone) - this client pass is a defensive mirror so the board is correct
  // even before the backend rolls out, keyed off the SAME instant the card shows
  // (scheduledFor → estimatedReadyAt → createdAt). `fromApi`/`DateTime.now`
  // resolve in Settings.defaultZone (restaurant tz, set in BootstrapGate), so a
  // plain startOf/endOf('day') is the restaurant's calendar day, not the device's.
  const now = DateTime.now();
  const todayStart = now.startOf('day');
  const todayEnd = now.endOf('day');
  const orders = (data?.items ?? []).filter((o) => {
    const when = fromApi(orderWhenIso(o));
    return when >= todayStart && when <= todayEnd;
  });

  if (isLoading) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="exclamationmark_triangle"
        title={t.orders.emptyState.loadError.title}
        description={t.orders.emptyState.loadError.description}
      />
    );
  }
  if (orders.length === 0) {
    const filtered = filters.status !== undefined || filters.type !== undefined;
    return (
      <EmptyState
        icon="bag"
        title={t.orders.emptyState.noOrders.title}
        description={
          filtered
            ? t.orders.emptyState.noOrders.filteredDescription
            : t.orders.emptyState.noOrders.defaultDescription
        }
      />
    );
  }

  // Day-overview: an orders-by-hour load histogram (reused CoversByHour). Shown
  // only when the list is unfiltered - a status-filtered list is one status's
  // subset, not the load's shape. The board is scoped to the restaurant's local
  // today (server-side, by service instant), so the summary reflects exactly that
  // list, bucketed by the hour each order lands (scheduled slot → ready ETA →
  // placed) so a bar agrees with its card's time.
  // Count is the load unit (orders have no party size); cancelled never happened.
  const countByHour = new Map<number, number>();
  let loadCount = 0;
  for (const o of orders) {
    if (o.status === 'CANCELLED') continue;
    countByHour.set(
      fromApi(orderWhenIso(o)).hour,
      (countByHour.get(fromApi(orderWhenIso(o)).hour) ?? 0) + 1,
    );
    loadCount++;
  }
  const showChart = filters.status === undefined && loadCount > 0;

  // Full day (00:00–23:00) so quieter hours stay visible, never trimmed. Hours
  // already passed are muted by the now-marker; the rest read as still to come.
  const bars: HourBar[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    guests: countByHour.get(hour) ?? 0,
  }));
  const nowHourFloat = now.hour + now.minute / 60;

  // Peak among upcoming hours (for the accessible label) and the next-2h load
  // (shown in words) - the rush to prepare for, not one already handled.
  let peakHour = -1;
  let peakCount = 0;
  let soonCount = 0;
  for (const { hour, guests: c } of bars) {
    if (hour >= now.hour && c > peakCount) {
      peakHour = hour;
      peakCount = c;
    }
    if (hour >= now.hour && hour < now.hour + 2) soonCount += c;
  }
  const peakLabel =
    peakHour >= 0
      ? `${String(peakHour).padStart(2, '0')}:00–${String((peakHour + 1) % 24).padStart(2, '0')}:00`
      : '';
  const chartAria =
    t.orders.ordersByHour(loadCount) +
    (peakHour >= 0 ? t.orders.chartBusiest(peakLabel, peakCount) : '');

  // Default (BASELINE) per-slot caps, per channel. Shown as two explicit figures
  // - never merged - because the bars pool both channels, so each cap governs
  // only its own share of a bar. Order shifts here are baseline-only (no WINDOW
  // shifts configured), so there are no per-shift bands to draw under the axis.
  const caps = [
    { label: t.orders.type.PICKUP, cap: baselineOrderCap(pickupShifts) },
    { label: t.orders.type.DELIVERY, cap: baselineOrderCap(deliveryShifts) },
  ].filter((c): c is { label: string; cap: number } => c.cap != null);

  return (
    <>
      {/* Background-refetch indicator (filter switch / 20s poll / post-mutation
          refresh). A zero-height sticky wrapper floats the spinner over the top
          of the list so it never shifts the content; the blocking first-load
          spinner is handled earlier by the isLoading branch. */}
      {isFetching && !isLoading && (
        <div style={{ position: 'sticky', top: 0, height: 0, zIndex: 5 }}>
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--f7-card-bg-color, #fff)',
              borderRadius: 999,
              padding: '6px 10px',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Preloader size={16} />
          </div>
        </div>
      )}
      {showChart && (
        <div
          style={{
            margin: '16px 16px 8px',
            padding: '12px 16px',
            background: 'var(--f7-card-bg-color, #fff)',
            borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {t.orders.ordersCount(loadCount)}
          </div>
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--f7-text-color-secondary, #8e8e93)',
              }}
            >
              {t.orders.ordersByHourLabel}
            </span>
            {caps.length > 0 && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--f7-text-color-tertiary, #a1a1a6)',
                  whiteSpace: 'nowrap',
                }}
              >
                {caps.map((c) => t.orders.capacityPerHour(c.cap, c.label)).join(' · ')}
              </span>
            )}
          </div>
          <CoversByHour
            bars={bars}
            nowHour={now.hour}
            nowOffsetHours={nowHourFloat}
            nowLabel={t.orders.now}
            ariaLabel={chartAria}
          />
          {soonCount > 0 && (
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: 'var(--f7-text-color-secondary, #8e8e93)',
              }}
            >
              {t.orders.nextTwoHours(soonCount)}
            </div>
          )}
        </div>
      )}

      {STATUS_ORDER.map((status) => {
        // Every section reads chronologically by the instant the card shows
        // (scheduledFor → estimatedReadyAt → createdAt), earliest first - the
        // backend's closure-timestamp sort doesn't match that displayed time.
        const group = orders
          .filter((o) => o.status === status)
          .sort(
            (a, b) => fromApi(orderWhenIso(a)).toMillis() - fromApi(orderWhenIso(b)).toMillis(),
          );
        return { status, label: t.orders.status[status], group };
      })
        .filter((s) => s.group.length > 0)
        .map(({ status, label, group }, sectionIndex) => (
          <Fragment key={status}>
            <BlockTitle medium style={{ marginTop: sectionIndex === 0 ? 24 : 12 }}>
              {t.orders.sectionCount(label, group.length)}
            </BlockTitle>
            <List dividers style={{ marginBottom: 8 }}>
              {/* Invoked as a function (not <OrderCard/>) so each child is a
                  direct ListItem - otherwise F7's <List> renders the row outside
                  its <ul> and the default <li> bullet leaks through. See
                  OrderCard / FRONTEND_PRACTICES Rule 9. */}
              {group.map((order) => {
                const km = deliveryDistanceKmPreferRoute(order, restaurant);
                return OrderCard({
                  order,
                  t,
                  onTap: onSelectOrder,
                  onBadgeTap: handleBadgeTap,
                  isSelected: order.id === selectedId,
                  distanceLabel: km == null ? undefined : formatDistanceKm(km, locale),
                });
              })}
            </List>
          </Fragment>
        ))}

      <OrderActions
        ref={popoverRef}
        order={actionTarget}
        onClose={() => f7.popover.close()}
        onAccept={handleAccept}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />

      <AcceptOrderSheet order={acceptTarget} from="list" onClose={() => setAcceptTarget(null)} />
      <CancelOrderSheet order={cancelTarget} from="list" onClose={() => setCancelTarget(null)} />
    </>
  );
}
